import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, Download } from 'lucide-react';
import { getRecommendedVisualization } from '@/lib/typeformParser';
import PieChart from './visualizations/PieChart';
import FrequencyBar from './visualizations/FrequencyBar';
import TextResponseList from './visualizations/TextResponseList';
import StatsSummary from './visualizations/StatsSummary';

/**
 * QuestionCard Component
 * Displays a single survey question with appropriate visualization
 * Handles expand/collapse and different question types
 */
const QuestionCard = ({ question, index, totalQuestions, onExport }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    question: questionText,
    type: questionType,
    responses,
    responseCount,
    analysis
  } = question;

  // Skip statement-type questions (section headers)
  if (questionType === 'statement') {
    return null;
  }

  // Get recommended visualization
  const vizType = getRecommendedVisualization({
    type: questionType,
    title: questionText,
    properties: question.properties || {}
  });

  // Determine if question should be collapsible
  const isCollapsible = vizType === 'text-list' || responseCount > 10;

  const renderVisualization = () => {
    if (!analysis || responseCount === 0) {
      return (
        <div className="text-center py-4 text-muted-foreground">
          No responses received
        </div>
      );
    }

    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log(`Question "${questionText}":`, {
        type: questionType,
        vizType,
        analysis,
        responseCount,
        responses: responses?.slice(0, 3)
      });
      
      // Extra debug for multiple choice questions showing "not available"
      if (questionType === 'multiple_choice' && vizType === 'pie-chart') {
        console.log(`MULTIPLE CHOICE DEBUG for "${questionText}":`, {
          analysisType: analysis?.type,
          analysisKeys: analysis ? Object.keys(analysis) : 'no analysis',
          distribution: analysis?.distribution,
          firstFewResponses: responses?.slice(0, 5),
          allResponseTypes: responses?.map(r => typeof r),
        });
      }
    }

    switch (vizType) {
      case 'pie-chart':
        return <PieChart data={analysis} questionType={questionType} />;
      
      case 'bar-chart':
        return <FrequencyBar data={analysis} questionType={questionType} />;
      
      case 'text-list':
        return (
          <TextResponseList 
            responses={responses} 
            isExpanded={isExpanded}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
          />
        );
      
      case 'summary':
        return <StatsSummary data={analysis} questionType={questionType} />;
      
      default:
        return (
          <div className="text-center py-4 text-muted-foreground">
            <p>Visualization not available for {questionType} questions</p>
            <p className="text-sm mt-1">{responseCount} responses received</p>
          </div>
        );
    }
  };

  const getQuestionTypeBadge = () => {
    const typeColors = {
      'multiple_choice': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'yes_no': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'short_text': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'long_text': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'number': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      'opinion_scale': 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
      'rating': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
    };

    const color = typeColors[questionType] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    
    return (
      <Badge variant="secondary" className={color}>
        {questionType.replace('_', ' ')}
      </Badge>
    );
  };

  const handleExport = () => {
    if (onExport) {
      onExport(question);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-muted-foreground">
                Q{index + 1}/{totalQuestions}
              </span>
              {getQuestionTypeBadge()}
            </div>
            <CardTitle className="text-base font-medium leading-tight">
              {questionText}
            </CardTitle>
          </div>
          
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Export button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              className="p-2"
              title="Export responses"
            >
              <Download className="h-4 w-4" />
            </Button>
            
            {/* Expand/collapse button for collapsible questions */}
            {isCollapsible && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-2"
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        {renderVisualization()}
        {/* Show transformation note for experience data below the chart */}
        {analysis?.isExperienceData && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <span className="font-medium">📊 Data Transformation:</span> Years converted to management experience ranges based on current year ({new Date().getFullYear()})
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QuestionCard;